import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import VesselTrackingMap from "./VesselTrackingMap";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const SAILINGS_API = `${API_BASE}/sailings`;
const VESSELS_API = `${API_BASE}/vessels`;

export default function CustomerPortalSailings() {
  const [sailings, setSailings] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(SAILINGS_API).then(r => r.json()),
      fetch(VESSELS_API).then(r => r.json()),
    ])
      .then(([sailingData, vesselData]) => {
        const sailingList = Array.isArray(sailingData) ? sailingData : (sailingData?.data || []);
        const vesselList = Array.isArray(vesselData) ? vesselData : (vesselData?.data || []);

        // Only show sailings customers care about — open for bookings, currently
        // loading, departed (en route), or arrived (confirms it made it). Hide
        // drafts (not confirmed yet) and closed (fully wrapped up, no longer relevant).
        const relevant = sailingList
          .filter((s) => ["open", "loading", "departed", "arrived"].includes(s.status))
          .sort((a, b) => new Date(a.etd || 0) - new Date(b.etd || 0));

        setSailings(relevant);
        setVessels(vesselList);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const findVessel = (sailing) => {
    const vesselId = sailing.vessel?._id || sailing.vessel;
    return vessels.find(v => v._id === vesselId || v.name === sailing.vesselName);
  };

  return (
    <div className="w-full p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sailing Schedule</h1>
        <p className="text-sm text-gray-500 mt-1">Upcoming sailings open for bookings.</p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="px-5 py-12 text-center text-sm text-gray-400">Loading...</div>
        ) : sailings.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-gray-400">No upcoming sailings open for bookings right now. Contact McCallum Bros for more information.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Sailing Ref</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Route</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Vessel</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">ETD</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">ETA</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Booking Cutoff</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600"></th>
              </tr>
            </thead>
            <tbody>
              {sailings.map((s, i) => {
                const vessel = findVessel(s);
                const canTrack = ["loading", "departed"].includes(s.status) && vessel?.mmsi;
                const isOpen = expandedId === s._id;

                return (
                  <React.Fragment key={s._id}>
                    <tr className={i > 0 ? "border-t border-gray-200" : ""}>
                      <td className="px-4 py-3 font-medium text-gray-900">{s.sailingRef}</td>
                      <td className="px-4 py-3 text-gray-600">{s.origin} → {s.destination}</td>
                      <td className="px-4 py-3 text-gray-600">{s.vesselName || "—"}</td>
                      <td className="px-4 py-3 text-gray-600">{s.etd ? new Date(s.etd).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "TBC"}</td>
                      <td className="px-4 py-3 text-gray-600">{s.eta ? new Date(s.eta).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "TBC"}</td>
                      <td className="px-4 py-3 text-gray-600">{s.bookingCutoff ? new Date(s.bookingCutoff).toLocaleDateString("en-NZ", { day: "2-digit", month: "short" }) : "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ${
                          s.status === "open" ? "bg-emerald-50 text-emerald-700" :
                          s.status === "loading" ? "bg-blue-50 text-blue-700" :
                          s.status === "departed" ? "bg-purple-50 text-purple-700" :
                          s.status === "arrived" ? "bg-slate-100 text-slate-600" :
                          "bg-amber-50 text-amber-700"
                        }`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canTrack && (
                            <button
                              onClick={() => setExpandedId(isOpen ? null : s._id)}
                              className="inline-flex w-32 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                            >
                              {isOpen ? "Hide Map" : "Track Vessel"}
                            </button>
                          )}
                          {s.status === "open" && (
                            <Link
                              to={`/portal/new?sailingId=${s._id}`}
                              className="inline-flex w-32 items-center justify-center gap-1.5 rounded-lg bg-slate-950 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
                            >
                              Request Booking
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && canTrack && (
                      <tr className="border-t border-gray-100 bg-gray-50">
                        <td colSpan={8} className="px-4 py-4">
                          <VesselTrackingMap mmsi={vessel.mmsi} vesselName={s.vesselName} height={320} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-gray-400">
        Bookings must be submitted before the cutoff date shown. Contact McCallum Bros directly for urgent requests.
      </p>
    </div>
  );
}